import { Controller } from 'stimulus'
import {
  colors, empty, esc, fmtDay, monthLabel, monthlyBars, placeTip, sparkline
} from '../lib/shared_dashboard/charts'

// Lab throughput shared dashboard (/d/<token>, SharedDashboards::LabThroughput).
// One JSON request per month range fills the tiles, the four monthly charts
// and the open jobs table; the table sorts client-side.
const MAX_MONTHS = 36
const DAY = 864e5
const daysBetween = (a, b) => Math.round((Date.parse(`${b.slice(0, 10)}T00:00:00Z`) - Date.parse(`${a.slice(0, 10)}T00:00:00Z`)) / DAY)
const n0 = v => v.toLocaleString('en-US')
const plural = (n, w) => `${n0(n)} ${w}${n === 1 ? '' : 's'}`
const sum = (rows, key) => rows.reduce((s, r) => s + r[key], 0)
const WARN = '<svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="currentColor"/>'
  + '<rect x="7.1" y="3.6" width="1.8" height="5.6" rx=".9" fill="white"/><circle cx="8" cy="11.6" r="1.05" fill="white"/></svg>'

// 'YYYY-MM' plus n months (n may be negative).
function addMonths(key, n) {
  const total = Number(key.slice(0, 4)) * 12 + Number(key.slice(5, 7)) - 1 + n
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

export default class extends Controller {
  static targets = [
    'from', 'to', 'preset', 'updated', 'stats', 'body', 'jobs', 'tasks', 'results', 'measurements',
    'openTitle', 'openSub', 'openRows', 'sort', 'tip'
  ];

  connect() {
    this.today = this.data.get('today')
    this.thisMonth = this.today.slice(0, 7)
    this.range = { from: `${this.today.slice(0, 4)}-01`, to: this.thisMonth }
    this.sortKey = 'due'
    this.tips = []
    this.fillMonths()
    this.syncInputs()

    this.onMove = e => placeTip(e, this.element, this.tipTarget, this.tips)
    this.onResize = () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => this.render(), 150) }
    document.addEventListener('pointermove', this.onMove)
    window.addEventListener('resize', this.onResize)
    this.load()
  }

  disconnect() {
    document.removeEventListener('pointermove', this.onMove)
    window.removeEventListener('resize', this.onResize)
  }

  // ---- inputs ----

  fillMonths() {
    const options = []
    for (let i = 0; i < MAX_MONTHS; i += 1) {
      const key = addMonths(this.thisMonth, -i)
      options.push(`<option value="${key}">${monthLabel(key)}${key === this.thisMonth ? ' (so far)' : ''}</option>`)
    }
    this.fromTarget.innerHTML = options.join('')
    this.toTarget.innerHTML = options.join('')
  }

  preset(event) {
    const { months } = event.currentTarget.dataset
    this.range = {
      from: months === 'year' ? `${this.thisMonth.slice(0, 4)}-01` : addMonths(this.thisMonth, -(Number(months) - 1)),
      to: this.thisMonth
    }
    this.syncInputs()
    this.load()
  }

  monthsChanged() {
    let from = this.fromTarget.value; let to = this.toTarget.value
    if (from > to) [from, to] = [to, from]
    this.range = { from, to }
    this.syncInputs()
    this.load()
  }

  sortOpen(event) {
    this.sortKey = event.currentTarget.dataset.sort
    this.sortTargets.forEach(b => b.classList.toggle('is-on', b === event.currentTarget))
    this.renderOpen()
  }

  // The selects show the range; a preset chip is lit when the range is its.
  syncInputs() {
    this.fromTarget.value = this.range.from
    this.toTarget.value = this.range.to
    this.presetTargets.forEach((b) => {
      const { months } = b.dataset
      const from = months === 'year' ? `${this.thisMonth.slice(0, 4)}-01` : addMonths(this.thisMonth, -(Number(months) - 1))
      b.classList.toggle('is-on', this.range.from === from && this.range.to === this.thisMonth)
    })
  }

  // ---- data ----

  async load() {
    const params = new URLSearchParams({ from: this.range.from, to: this.range.to })
    const seq = (this.seq || 0) + 1
    this.seq = seq
    this.bodyTarget.classList.add('sd-loading')
    this.statsTarget.classList.add('sd-loading')
    try {
      const res = await fetch(`${this.data.get('url')}?${params}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.status === 401) { window.location.reload(); return }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const payload = await res.json()
      if (seq !== this.seq) return
      this.payload = payload
      this.loadedAt = new Date()
      this.render()
    } catch (err) {
      if (seq === this.seq) this.updatedTarget.textContent = "Couldn't load the dashboard. Refresh the page to try again."
    } finally {
      if (seq === this.seq) {
        this.bodyTarget.classList.remove('sd-loading')
        this.statsTarget.classList.remove('sd-loading')
      }
    }
  }

  // ---- rendering ----

  render() {
    if (!this.payload) return
    this.tips = []
    this.c = colors(this.element)
    this.updatedTarget.textContent = `Updated ${this.loadedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Draft jobs aren't counted.`
    this.renderStats()
    this.renderCharts()
    this.renderOpen()
  }

  tip(html) {
    this.tips.push(html)
    return `data-sd-tip="${this.tips.length - 1}"`
  }

  renderStats() {
    const { months, open } = this.payload
    const { mark } = this.c
    const series = key => months.map(m => m[key])
    const judged = sum(months, 'on_time_judged'); const onTime = sum(months, 'on_time')
    const pct = judged ? Math.round((onTime / judged) * 100) : null
    const values = months.map(m => m.result_values + m.task_values)
    const full = months.filter(m => !m.partial)
    const perMonth = full.length ? Math.round(full.reduce((s, m) => s + m.result_values + m.task_values, 0) / full.length) : null
    const year = this.today.slice(0, 4)
    const openSub = [
      open.late ? `<b class="sd-bad">${n0(open.late)} past due</b>` : 'None past due',
      open.before_year ? `${n0(open.before_year)} received before ${year}` : null
    ].filter(Boolean).join(', ')
    this.statsTarget.innerHTML = `
      <div class="sd-stat"><span class="sd-stat-lab">Jobs completed</span><div class="sd-stat-row"><span class="sd-stat-val">${n0(sum(months, 'completed'))}</span>${sparkline(series('completed'), mark)}</div><span class="sd-delta">${plural(sum(months, 'received'), 'job')} received in this range</span></div>
      <a class="sd-stat" href="#open-jobs"><span class="sd-stat-lab">Jobs open <em>now</em></span><div class="sd-stat-row"><span class="sd-stat-val">${n0(open.total)}</span></div><span class="sd-delta">${openSub}</span></a>
      <div class="sd-stat"><span class="sd-stat-lab">On-time completion</span><div class="sd-stat-row"><span class="sd-stat-val">${pct == null ? 'n/a' : `${pct}<small>%</small>`}</span>${sparkline(months.map(m => (m.on_time_judged ? (m.on_time / m.on_time_judged) * 100 : null)), mark)}</div><span class="sd-delta">${judged ? `${n0(onTime)} of ${plural(judged, 'job')} with an estimated completion` : 'No completed job in this range had an estimated completion'}</span></div>
      <div class="sd-stat"><span class="sd-stat-lab">Tasks completed</span><div class="sd-stat-row"><span class="sd-stat-val">${n0(sum(months, 'tasks_completed'))}</span>${sparkline(series('tasks_completed'), mark)}</div><span class="sd-delta">${plural(sum(months, 'tasks_added'), 'task')} added in this range</span></div>
      <a class="sd-stat" href="#open-jobs"><span class="sd-stat-lab">Tasks open <em>now</em></span><div class="sd-stat-row"><span class="sd-stat-val">${n0(open.tasks)}</span></div><span class="sd-delta">on the ${plural(open.total, 'open job')}</span></a>
      <div class="sd-stat"><span class="sd-stat-lab">Measurements recorded</span><div class="sd-stat-row"><span class="sd-stat-val">${n0(sum(months, 'result_values') + sum(months, 'task_values'))}</span>${sparkline(values, mark)}</div><span class="sd-delta">${n0(sum(months, 'result_values'))} on results, ${n0(sum(months, 'task_values'))} on tasks${perMonth == null ? '' : `. About ${n0(perMonth)} a month`}</span></div>`
  }

  renderCharts() {
    const { months } = this.payload
    const { accent, mark } = this.c
    const tip = html => this.tip(html)
    monthlyBars(this.jobsTarget, months, [{ key: 'received', name: 'Received', color: accent }, { key: 'completed', name: 'Completed', color: mark }], this.c, tip)
    monthlyBars(this.tasksTarget, months, [{ key: 'tasks_added', name: 'Added', color: accent }, { key: 'tasks_completed', name: 'Completed', color: mark }], this.c, tip)
    monthlyBars(this.resultsTarget, months, [{ key: 'results_added', name: 'Added', color: accent }, { key: 'results_completed', name: 'Completed', color: mark }], this.c, tip)
    const measured = months.map(m => ({ ...m, measurements: m.result_values + m.task_values }))
    if (!measured.some(m => m.measurements)) { empty(this.measurementsTarget, 'No measurements were recorded in this range.'); return }
    monthlyBars(this.measurementsTarget, measured, [{ key: 'measurements', name: 'Recorded', color: mark }], this.c, tip,
      m => `<br><span class="sd-k">On results</span> ${n0(m.result_values)}<br><span class="sd-k">On tasks</span> ${n0(m.task_values)}`)
  }

  // 'Sep 23', with the year when it isn't this one.
  day(iso) {
    return iso.slice(0, 4) === this.today.slice(0, 4) ? fmtDay(iso) : `${fmtDay(iso)}, ${iso.slice(0, 4)}`
  }

  renderOpen() {
    if (!this.payload) return
    const { open } = this.payload
    const requesters = this.data.get('requesters') === 'true'
    const jobs = [...open.jobs]
    if (this.sortKey === 'received') jobs.sort((a, b) => a.received_at.localeCompare(b.received_at))
    const shown = open.total > jobs.length ? `Showing the ${open.limit} due soonest. ` : ''
    this.openTitleTarget.textContent = `Open now (${n0(open.total)})`
    this.openSubTarget.textContent = `${open.late ? `${plural(open.late, 'job')} past due. ` : ''}${shown}Every job not yet completed. The date range doesn't apply here.`
    if (!jobs.length) {
      this.openRowsTarget.innerHTML = `<tr><td colspan="${requesters ? 7 : 6}" class="sd-none">No open jobs.</td></tr>`
      return
    }
    this.openRowsTarget.innerHTML = jobs.map((j) => {
      let due = '<span class="sd-none">Not set</span>'
      if (j.due) {
        const diff = daysBetween(this.today, j.due)
        let sub = `in ${plural(diff, 'day')}`
        if (j.late_days) sub = `<span class="sd-late">${WARN}${plural(j.late_days, 'day')} late</span>`
        else if (diff === 0) sub = 'today'
        due = `${this.day(j.due)}<span class="sd-cell-sub">${sub}</span><span class="sd-cell-sub">${j.due_kind === 'requested' ? 'Requested' : 'Estimated'}</span>`
      }
      const age = daysBetween(j.received_at, this.today)
      const received = `${this.day(j.received_at)}<span class="sd-cell-sub">${age < 1 ? 'today' : `${plural(age, 'day')} ago`}</span>`
      const { total, done } = j.tasks
      const tasks = total
        ? `<div class="sd-prog"><i><span style="width:${Math.round((done / total) * 100)}%"></span></i>${n0(done)} of ${n0(total)}</div>`
        : '<span class="sd-none">No tasks yet</span>'
      return `<tr>
        <td class="sd-job"><b>#${esc(j.number)}</b>${esc(j.name || '')}</td>
        <td><span class="sd-pill${j.in_progress ? ' is-active' : ''}">${esc(j.status || 'No status')}</span></td>
        <td class="sd-num">${received}</td>
        <td class="sd-num">${due}</td>
        <td>${tasks}</td>
        <td class="sd-ppl">${j.assignees.map(esc).join('<br>') || '<span class="sd-none">None</span>'}</td>
        ${requesters ? `<td class="sd-ppl">${j.requesters.map(esc).join('<br>') || '<span class="sd-none">None</span>'}</td>` : ''}
      </tr>`
    }).join('')
  }
}
